import {
  Box,
  Button,
  FileUploadDropzone,
  FileUploadState,
  isAcceptedFileType,
  ListDriveInfo,
  UploadedFileInfo,
} from '@ouster/webviz-react-components';

import { useState } from 'react';

import { BASE_ORG_API } from '../../utils/constants';
import { fetchWithAuth } from '../../utils/requests';

const ACCEPTED_CONFIG_EXTENSIONS = ['json'];

const uploadFileWithProgress = (
  signedURL: string,
  file: File,
  onStart: () => void,
  updateCallback: (pctUploaded: number, ts: number) => void,
  completionCallback: () => void
) => {
  const xhr = new XMLHttpRequest();
  xhr.upload.addEventListener('progress', (evt) => {
    const fileSize = file.size;
    const pctUploaded = Math.min((evt.loaded / fileSize) * 100, 100); // Avoid over 100%? debug
    updateCallback(pctUploaded, Date.now());
  });
  xhr.addEventListener('load', completionCallback);
  xhr.open('PUT', `${signedURL}`);
  xhr.setRequestHeader('Content-Type', 'application/octet-stream');
  xhr.send(file);
  onStart();
  return xhr;
};

const estimateTimeLeft = (
  prevProgress: number,
  pctUploaded: number,
  timeDiff: number
) => {
  const pctDiff = pctUploaded - prevProgress;
  const pctPerSec = pctDiff / (timeDiff / 1000);
  const pctToGo = 100 - pctUploaded;
  const secondsToGo = pctToGo / pctPerSec;
  return Math.floor(secondsToGo);
};

export default function ReUploadExtrinsic({
  recording,
  orgId,
  reprocessRecording,
  onClose,
}: {
  recording: ListDriveInfo;
  orgId: number;
  reprocessRecording: (value: ListDriveInfo) => void;
  onClose: () => void;
}) {
  const [uploadState, setUploadState] = useState(FileUploadState.active);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [configInfo, setConfigInfo] = useState<UploadedFileInfo | undefined>(
    undefined
  );
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  const [timeOfLastConfigProgress, setTimeOfLastConfigProgress] =
    useState<number>(Date.now());
  const [timeToConfigUploadComplete, setTimeToConfigUploadComplete] =
    useState<number>(0);

  if (
    recording.source_prefix === undefined ||
    typeof recording.source_prefix !== 'string'
  ) {
    return null;
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-assignment
  const bucketId: string = recording.source_prefix.split('/')[1];

  const uploadExtrinsicFile = async (file: File, uploadSessionID: string) => {
    const configUploadUrl = `${BASE_ORG_API}/${orgId}/uploads/${uploadSessionID}/exfile/`;

    const response = await fetchWithAuth(configUploadUrl, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ name: file.name }),
    });
    const responseJson = (await response.json()) as {
      signed_url: string;
      uuid: string;
    };

    const signedUrl = responseJson.signed_url;
    setUploadState(FileUploadState.inProgress);

    const onUploadComplete = () => {
      setUploadState(FileUploadState.success);
    };

    uploadFileWithProgress(
      signedUrl,
      file,
      () => {
        setTimeOfLastConfigProgress(Date.now());
      },
      (pctUploaded, ts) => {
        const timeDiff = ts - timeOfLastConfigProgress;
        const timeToGo = estimateTimeLeft(
          uploadProgress,
          pctUploaded,
          timeDiff
        );
        const averagedTimeToGo = Math.floor(
          (timeToConfigUploadComplete + timeToGo) / 2
        );
        setUploadProgress(pctUploaded);
        setTimeOfLastConfigProgress(ts);
        setTimeToConfigUploadComplete(averagedTimeToGo);
      },
      onUploadComplete
    );
  };

  const onUpload = (files: File[]) => {
    const configFiles = files.filter((file) =>
      isAcceptedFileType(file, ACCEPTED_CONFIG_EXTENSIONS)
    );
    const uploadConfigFile = configFiles[0];

    if (uploadConfigFile.name !== 'extrinsic_parameters.json') {
      setErrorText(
        'The file you uploaded is not named extrinsic_parameters.json. To make sure we can properly reprocess your recording, please upload a file with the exact same name.'
      );
      setUploadState(FileUploadState.error);
      return;
    }

    setUploadState(FileUploadState.initializing);
    setConfigInfo({
      name: uploadConfigFile.name,
      lastModified: uploadConfigFile.lastModified,
      size: uploadConfigFile.size,
    });

    uploadExtrinsicFile(uploadConfigFile, bucketId);
  };

  const onRemoval = () => {
    setUploadState(FileUploadState.active);
    setConfigInfo(undefined);
    setUploadProgress(0);
  };

  const onSubmitHandler = () => {
    reprocessRecording(recording);
    onClose();
  };

  return (
    <Box width="100%" maxW={560} display="grid" rowGap={8} data-testid="reupload-extrinsic-root">
      <Box display="grid" rowGap={2}>
        <p>
          Please drag and drop your latest extrinsic file:
          extrinsic_parameters.json.
        </p>
        <p>
          <small>
            <b>NOTE:</b> The file you upload should have the exact same name.
          </small>
        </p>
      </Box>
      <Box minH={250}>
        <FileUploadDropzone
          uploadState={uploadState}
          uploadProgress={uploadProgress}
          uploadTimeEstimate={timeToConfigUploadComplete}
          onUpload={onUpload}
          onRemoval={onRemoval}
          errorText={errorText}
          acceptedFormats={ACCEPTED_CONFIG_EXTENSIONS}
          orgUploadLimitExceed={false}
          fileInfo={configInfo}
          bugSubmissionUrl=""
          fileType=".json"
          titleString=" "
          data-testid="reupload-extrinsic-dropzone"
        />
      </Box>
      <Button
        minWidth={150}
        justifySelf="end"
        mt={4}
        isDisabled={uploadState !== FileUploadState.success}
        onClick={onSubmitHandler}
        data-testid="reupload-extrinsic-submit-btn"
      >
        Submit
      </Button>
    </Box>
  );
}
